
import React, { useCallback, useMemo } from "react";
import styles from "./ExcelPasteGrid.module.css";

export type ExcelPasteGridValue = {
  cells: string[][];
};

type Props = {
  value: ExcelPasteGridValue;
  onChange: (next: ExcelPasteGridValue) => void;
  minRows?: number;
  minCols?: number;
  placeholder?: string;
  /** Number of top rows that are read-only (useful for header rows). */
  lockedRows?: number;
  /** Show the +Row / +Col buttons. Defaults to true. */
  showAddRowCol?: boolean;
  /** Show the Clear button. Defaults to true. */
  showClear?: boolean;
  /** Show a Copy button to export TSV. Defaults to true. */
  showCopy?: boolean;
  /** Set of row indices to visually highlight (e.g. current reporting period). */
  highlightRows?: Set<number>;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function parseClipboard(e: React.ClipboardEvent): string[][] {
  const dt = e.clipboardData;
  const html = dt.getData("text/html");
  const text = dt.getData("text/plain");

  // Try HTML table first (Excel usually provides this)
  if (html && /<table/i.test(html)) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const table = doc.querySelector("table");
    if (table) {
      const rows = Array.from(table.querySelectorAll("tr"));
      const grid: string[][] = [];
      for (const r of rows) {
        const cells = Array.from(r.querySelectorAll("th,td")).map((c) =>
          (c.textContent || "").replace(/\r\n/g, "\n").trim()
        );
        // Skip entirely empty rows
        if (cells.some((c) => String(c).trim().length > 0)) grid.push(cells);
      }
      if (grid.length) return grid;
    }
  }

  // Fallback: plain text as TSV
  const lines = (text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((l) => l.length > 0);

  const grid = lines.map((l) => l.split("\t"));
  // If there are no tabs at all, treat as single column
  return grid;
}

function ensureSize(cells: string[][], minRows: number, minCols: number): string[][] {
  const r = Math.max(minRows, cells.length);
  const c = Math.max(minCols, ...cells.map((row) => row.length), 0);
  const next: string[][] = [];
  for (let i = 0; i < r; i++) {
    const row = cells[i] ? [...cells[i]] : [];
    while (row.length < c) row.push("");
    next.push(row);
  }
  return next;
}

export function cellsToTsv(cells: string[][]): string {
  const trimmedRows = cells
    .map((row) => row.map((v) => String(v ?? "").trimEnd()))
    .filter((row) => row.some((v) => String(v).trim().length > 0));

  if (!trimmedRows.length) return "";

  // trim trailing empty columns per row for cleaner TSV
  const cleaned = trimmedRows.map((row) => {
    let last = row.length - 1;
    while (last >= 0 && String(row[last]).trim() === "") last--;
    return row.slice(0, Math.max(0, last + 1));
  });

  return cleaned.map((row) => row.join("\t")).join("\n") + "\n";
}

export function tsvToCells(tsv: string): string[][] {
  const lines = (tsv || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((l) => l.trim().length > 0);
  if (!lines.length) return [];
  return lines.map((l) => l.split("\t"));
}

const ExcelPasteGrid: React.FC<Props> = ({
  value,
  onChange,
  minRows = 6,
  minCols = 6,
  placeholder,
  lockedRows = 0,
  showAddRowCol = true,
  showClear = true,
  showCopy = true,
  highlightRows,
}) => {
  const sized = useMemo(() => ensureSize(value.cells || [], minRows, minCols), [value.cells, minRows, minCols]);

  
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();

      const parsedRaw = parseClipboard(e);

      // Preserve locked header rows and paste below them.
      const header = lockedRows > 0 ? sized.slice(0, lockedRows).map((r) => [...r]) : [];

      // Determine expected column count from the first row (header if present, else current grid).
      const expectedCols =
        (header[0]?.length || sized[0]?.length || 0);

      // If the clipboard includes a header row that matches the locked header, drop it.
      let parsed = parsedRaw;
      if (lockedRows > 0 && header.length && parsedRaw.length) {
        const h = header[0].map((x) => String(x || "").trim().toLowerCase());
        const p0 = parsedRaw[0].map((x) => String(x || "").trim().toLowerCase());
        const matchesHeader =
          h.length > 0 &&
          p0.length >= h.length &&
          h.every((v, i) => v === p0[i]);
        if (matchesHeader) parsed = parsedRaw.slice(1);
      }

      // Heuristic: sometimes Excel/Sheets paste can "split" logical rows into two
      // (e.g. wrapped text/newlines). Merge rows where one has only col0 and the next
      // has the remaining columns.
      const normRow = (r: string[]) => {
        const rr = [...r];
        while (expectedCols && rr.length < expectedCols) rr.push("");
        return rr;
      };

      const merged: string[][] = [];
      for (let i = 0; i < parsed.length; i++) {
        const cur = normRow(parsed[i] || []);
        const curName = String(cur[0] || "").trim();
        const curHasName = curName.length > 0;
        const curMissingDate = String(cur[1] || "").trim().length === 0;

        if (curHasName && curMissingDate && i + 1 < parsed.length) {
          const nxt = normRow(parsed[i + 1] || []);
          const nxtName = String(nxt[0] || "").trim();
          const nxtHasDate = String(nxt[1] || "").trim().length > 0;

          // Case A: the next row is a continuation row (name empty) that has the remaining columns.
          // Case B: the next row contains the remainder of a wrapped name *and* the remaining columns
          // (Excel HTML tables can surface wrapped line breaks as a separate row where col0 is not empty).
          if (nxtHasDate) {
            const combined = [...cur];

            // If the next row also has name text, treat it as a continuation of the project name.
            if (nxtName) {
              combined[0] = `${curName} ${nxtName}`.trim();
            }

            for (let c = 1; c < combined.length; c++) {
              if (!String(combined[c] || "").trim() && String(nxt[c] || "").trim()) {
                combined[c] = nxt[c];
              }
            }

            merged.push(combined);
            i++; // skip next row
            continue;
          }
        }

        // Skip entirely empty rows
        if (cur.some((c) => String(c || "").trim().length > 0)) merged.push(cur);
      }

      const body = merged.length ? merged : parsed.map(normRow);

      const nextCells = [...header, ...body];

      // Ensure minimum size and consistent column sizing.
      const finalCells = ensureSize(
        nextCells,
        Math.max(minRows, nextCells.length),
        Math.max(minCols, expectedCols, ...nextCells.map((r) => r.length), 0)
      );

      onChange({ cells: finalCells });
    },
    [lockedRows, minCols, minRows, onChange, sized]
  );

  const handleCell = useCallback(
    (r: number, c: number, v: string) => {
      if (lockedRows > 0 && r < lockedRows) return;
      const next = sized.map((row) => [...row]);
      next[r][c] = v;
      onChange({ cells: next });
    },
    [lockedRows, onChange, sized]
  );

  const addRow = useCallback(() => {
    const next = sized.map((row) => [...row]);
    next.push(Array.from({ length: next[0]?.length || minCols }, () => ""));
    onChange({ cells: next });
  }, [minCols, onChange, sized]);

  const addCol = useCallback(() => {
    const next = sized.map((row) => [...row, ""]);
    onChange({ cells: next });
  }, [onChange, sized]);

  const clear = useCallback(() => {
    // "Clear" should never wipe locked header rows (common for import sheets).
    // Instead, preserve the first `lockedRows` and clear everything below.
    const next = sized.map((row) => row.map(() => ""));
    if (lockedRows > 0) {
      for (let r = 0; r < Math.min(lockedRows, sized.length); r++) {
        next[r] = [...sized[r]];
      }
    }
    onChange({ cells: ensureSize(next, minRows, minCols) });
  }, [lockedRows, minRows, minCols, onChange, sized]);


const copyTsv = useCallback(async () => {
  const tsv = sized.map((r) => r.join("\t")).join("\n");
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(tsv);
      return;
    }
  } catch {
    // fall back below
  }
  // Fallback: temporary textarea + execCommand
  const ta = document.createElement("textarea");
  ta.value = tsv;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  ta.style.top = "0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }
}, [sized]);

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <div className={styles.hint}>
          {placeholder || "Click in the grid and paste directly from Excel/Sheets (⌘V)."}
        </div>
        <div className={styles.actions}>
          {showAddRowCol ? (
            <>
              <button type="button" className={styles.btn} onClick={addRow}>
                + Row
              </button>
              <button type="button" className={styles.btn} onClick={addCol}>
                + Col
              </button>
            </>
          ) : null}
          {showCopy ? (
            <button type="button" className={styles.btn} onClick={copyTsv}>
              Copy
            </button>
          ) : null}
          {showClear ? (
            <button type="button" className={styles.btnDanger} onClick={clear}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.gridWrap} onPaste={handlePaste}>
        <table className={styles.grid}>
          <tbody>
            {sized.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c}>
                    <input
                      className={[
                        styles.cell,
                        r < lockedRows ? styles.cellLocked : '',
                        highlightRows?.has(r) ? styles.cellHighlight : '',
                      ].filter(Boolean).join(' ')}
                      value={cell}
                      onChange={(e) => handleCell(r, c, e.target.value)}
                      readOnly={r < lockedRows}
                      spellCheck={false}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExcelPasteGrid;